import type { Recording, RecordingFrame } from "@/api/types";
import { pluralize } from "@/lib/format";

export interface Tally {
  /** Frames with an outcome, of `total` the video will give (planned, or sampled so far). */
  measured: number;
  total: number;
  match: number;
  uncertain: number;
  new: number;
  noSignal: number;
}

export function tally(recording: Recording): Tally {
  const count = (pred: (f: RecordingFrame) => boolean) => recording.frames.filter(pred).length;
  return {
    measured: count((f) => f.status !== "processing"),
    total: Math.max(recording.planned ?? 0, recording.frames.length),
    match: count((f) => f.status === "match"),
    uncertain: count((f) => f.status === "uncertain"),
    new: count((f) => f.status === "new"),
    noSignal: count((f) => f.status === "no_shark" || f.status === "error"),
  };
}

/** The sampled frame nearest a time in the video (frames sit mid-window), or -1 if none. */
export function frameAt(frames: RecordingFrame[], seconds: number): number {
  let best = -1;
  for (const [i, frame] of frames.entries()) {
    if (best < 0 || Math.abs(frame.time - seconds) < Math.abs(frames[best]!.time - seconds)) {
      best = i;
    }
  }
  return best;
}

/**
 * The recording's verdict, in the words of the page title. Frames are judged one by one, and
 * one frame of a video can match wrongly (a handful of spots on a blurred flank), so a
 * recording with both matched frames and frames unknown to the register names nobody.
 */
export function headline(recording: Recording): string {
  if (recording.status === "processing") return "Reading the video, frame by frame.";
  if (recording.status === "error") return "The video could not be read.";
  const matched = recording.sharks.filter((s) => s.matched > 0);
  const t = tally(recording);
  if (matched.length > 0 && t.new > 0) return "The frames disagree";
  if (matched.length === 1) return matched[0]!.shark.display_name;
  if (matched.length > 1) return `${matched.length} individuals identified`;
  if (t.new > 0) return "A shark not on record";
  if (recording.sharks.length > 0) return "No confident match";
  return "No shark identified";
}

/** One line on what the frames showed, for the result head. */
export function summary(recording: Recording): string {
  const t = tally(recording);
  const parts = [
    t.match && `${t.match} matched`,
    t.uncertain && `${t.uncertain} inconclusive`,
    t.new && `${t.new} unknown to the register`,
    t.noSignal && `${t.noSignal} without a readable shark`,
  ].filter(Boolean);
  const of = `${t.measured} of ${pluralize(t.total, "frame")} measured`;
  return parts.length ? `${of}: ${parts.join(", ")}.` : `${of}.`;
}
